import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAssistRetry } from "./AppAssistRetry";
import { AppAssistAnswer, AppAssistApi } from "./AppAssistApi";
import { AppAssistEntry, AppAssistRules, AppAssistSession } from "./AppAssistRules";

// AppAssistForget - "Remove from history" on a saved question in the AI panel (owner-approved 2026-10-01): the
// button's words, the inline confirm, and the one request in flight per entry. `AppAssistPanel` draws the row
// under each SAVED entry; the service takes the whole turn out of the contact's `ai-chat.json` (`ForgetRun`).
//
// ⭐ WHY: a saved answer can be wrong, and the history is read back to the AI on every later question. The file
// cannot be fixed by hand in Dropbox - the service keeps its own copy and writes Dropbox-first - so the panel asks.
// ⛔ TWO CLICKS, BOTH THE OWNER'S: the bin shows "Remove this answer?" in the entry; only its Remove asks the
// service. The bin alone removes nothing, and nothing but that click can.
// ⛔ NOTHING HERE SENDS A MESSAGE - the request is `/assist {action:"forget", id}`: an id, no text. And a removal
// never makes an OLDER draft sendable (`AppAssistRules.Retire`), so the click cannot land on a Send it uncovered.
// ⚠ OPTIMISTIC, HONEST: the confirm click fades the entry and says "Removing…" at once; the service's answer takes
// it out of the log. A refusal puts it back exactly as it was, with the service's own sentence in ink under it
// (law 1: validation), never the handler. A `retry` refusal (the archive still being restored after a restart) is
// asked again quietly, like every other panel action.
// ⚠ Pure of browser globals - the Bridge compiles the panel (`AssistContract.test.ts`).

/** What a removal asks of the page around the panel (the panel's own host). */
export interface IForgetHost {
    Post(Body: string, Done: (Status: number, Text: string) => void): void;
    Later?(Ms: number, Then: () => void): void;
    /**
     * A removal ended. `Removed` true = the turn is out of the history (tell the other tabs, update the count);
     * false = it was refused and the entry is back. Either way: repaint WHERE THE OWNER IS, not at the log's end.
     * Optional - a host without it is told through `Answered` (a plain repaint).
     */
    Forgot?(Session: AppAssistSession, Removed: boolean): void;
    Answered(Session: AppAssistSession): void;
}

/** One "Remove from history" in flight - answered once, into the session it was asked in. */
export class AppAssistForgetCall {
    /** Quiet re-asks after `retry` refusals (the archive still being restored after a restart). */
    public static readonly MaxAgain: number = AppAssistRetry.MaxAgain;
    private Session: AppAssistSession;
    /** The removed entry's history ids as they were at the click - a reload replaces the entry OBJECTS. */
    private Ids: string[];
    private Host: IForgetHost;
    private Body: string;
    private Retry: AppAssistRetry = new AppAssistRetry();
    /** The clock (ms) - a field so the suite can hold it still. */
    public Now: () => number = AppAssistForget.WallNow;

    public constructor(Session: AppAssistSession, Ids: string[], Host: IForgetHost, Body: string) {
        this.Session = Session;
        this.Ids = Ids.slice();
        this.Host = Host;
        this.Body = Body;
    }

    /** `AppAssistDone`: removed, asked again quietly (`retry`), or the entry back with the service's sentence. */
    public OnDone = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Retry.IsEnded() === true) { return; }
            const Heard: AppAssistAnswer = AppAssistApi.Parse(Status, Text, AppAssistForget.LostWhy());
            if (this.Retried(Heard) === true) { return; }
            this.Retry.End();
            if (Heard.Ok === true) {
                AppAssistRules.Forgot(this.Session, this.Ids, this.Now());
                this.Tell(true);
                return;
            }
            const Entry: AppAssistEntry | null = AppAssistRules.EntryWith(this.Session, this.Ids);
            if (Entry !== null) { Entry.Forget = ""; Entry.ForgetWhy = Heard.Why; }
            this.Tell(false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistForgetCall.OnDone");
        }
    };

    private Tell = (Removed: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Host.Forgot !== undefined) { this.Host.Forgot(this.Session, Removed); return; }
            this.Host.Answered(this.Session);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistForgetCall.Tell");
        }
    };

    /** ⭐ THE RESTART WINDOW: the same body again after `retry` seconds - the entry stays faded, nothing final is said. */
    private Retried = (Heard: AppAssistAnswer): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Host.Later === undefined) { return false; }
            const Wait: number = this.Retry.WaitFor(Heard);
            if (Wait <= 0) { return false; }
            this.Retry.Schedule(Wait, this.Host, this.Again);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistForgetCall.Retried");
            return false;
        }
    };

    private Again = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Retry.IsEnded() === true) { return; }
            this.Host.Post(this.Body, this.OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistForgetCall.Again");
        }
    };
}

export class AppAssistForget {
    /** The log buttons' `ActAttr` values: the bin, the confirm's Remove, the confirm's Cancel. */
    public static readonly AskAct: string = "forgetask";
    public static readonly Act: string = "forget";
    public static readonly CancelAct: string = "forgetno";
    /** The bin and the confirm's Remove name the history item they stand for (an id, never text) - a harness targets one entry by it. */
    public static readonly ItemAttr: string = "data-wac-item";
    /** The entry's states (`AppAssistEntry.Forget`). */
    public static readonly Asking: string = "ask";
    public static readonly Going: string = "going";
    /**
     * ⛔ For this long after an entry left the log, a click on a draft's Send does nothing: the log has just moved
     * under the pointer, and a second click meant for "Remove" must not become a message.
     */
    public static readonly SendGuardMs: number = 800;

    /** DOCUMENTED EXCEPTION: the wall clock. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static WallNow = (): number => { return Date.now(); };

    /**
     * Whether `Entry` offers the bin: the owner's browser (`Allowed`), "ask AI about this chat" (never another
     * question mode), and an entry that is in the saved history (`AppAssistRules.CanForget`).
     * DOCUMENTED EXCEPTION: pure decision over values in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static Offered = (Allowed: boolean, InMode: boolean, Entry: AppAssistEntry): boolean => {
        return Allowed === true && InMode === false && AppAssistRules.CanForget(Entry) === true;
    };

    /** Whether a draft's Send is still held back after a removal (`SendGuardMs`). DOCUMENTED EXCEPTION: pure arithmetic. */
    public static SendHeld = (Session: AppAssistSession, NowMs: number): boolean => {
        return Session.ForgotAtMs > 0 && NowMs - Session.ForgotAtMs >= 0 && NowMs - Session.ForgotAtMs < AppAssistForget.SendGuardMs;
    };

    /** The id the request names - any item of the turn does; the first is its question. DOCUMENTED EXCEPTION: pure read. */
    public static IdOf = (Entry: AppAssistEntry): string => {
        if (Entry.Ids.length === 0) { return ""; }
        return nz(Entry.Ids[0]);
    };

    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static Label = (): string => { return "Remove from history"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static Question = (): string => { return "Remove this answer?"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static YesLabel = (): string => { return "Remove"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static NoLabel = (): string => { return "Cancel"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static GoingNote = (): string => { return "Removing…"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static SignInNote = (): string => { return "Sign in on this browser to remove answers from the history."; };
    /** ⚠ CALM: the entry is still there, and the owner can press again. DOCUMENTED EXCEPTION: pure constant. */
    public static LostWhy = (): string => {
        return "Couldn't reach WACommander just now, so nothing was removed - try again in a moment.";
    };
}
